import moment from 'moment';
import { KrpanoTypes } from '../types/KrpanoTypes';
import { Media } from '../types/CommonTypes';
import Tour from '../model/Tour';
import {
  DEFAULT_LASER_RANGE,
  GALLERY_TYPE,
  ITEM_INFO_TEMPLATE,
  ITEM_INFO_TYPES,
  LASER_RANGE_VALUE,
  LASER_TOOLTIP_SLASH_MAX_SIZE,
  OVERVIEW_POSITION_POI,
  POI_TYPE,
  POI_Z_ORDER,
  POPUP_ELEMENT_SIZE,
  POSITION_POI,
} from '../commons/constants/constants';
import { UNIT } from '../commons/constants/enum';
import { convertCurrencyVnd, getVideoCDNUrl } from './functionUtils';
import { getImageCDNUrl } from './imageUtils';
import Poi from '../model/Poi/Poi';
import { DEVICE_VIEWER_ASPECT } from '../commons/constants/constants';

export const isMobileDevice = (krpano: KrpanoTypes) => {
  let checked = false;
  if (krpano) {
    const mobile = krpano.get('device.mobile');
    const tablet = krpano.get('device.tablet');
    checked = mobile || tablet;
  }
  return checked;
};

const initImageStripPoiIcon = (
  krpano: KrpanoTypes,
  name: string,
  icon?: Media,
  frameRate?: number
) => {
  if (icon && icon.file) {
    krpano.set(`imageWidth_${name}`, icon.file.width);
    krpano.set(`imageHeight_${name}`, icon.file.height);
    krpano.set(`frameWidth_${name}`, icon.frameWidth);
    krpano.set(`frameHeight_${name}`, icon.frameHeight);
  }

  if (frameRate) {
    krpano.set(`frameRate_${name}`, frameRate);
  }
};

export const reInitImageStrip = (
  krpano: KrpanoTypes,
  name: string,
  icon: Media,
  frameRate: number
) => {
  initImageStripPoiIcon(krpano, name, icon, frameRate);
  krpano.call(`set(hotspot[${name}].onloaded, ${imageStripAnimationPoiIcon(name)});`);
};

export const imageStripAnimationPoiIcon = (name: string): string => `
  def(frame_${name}, integer, 0);
  calc(xFrames_${name}, (imageWidth_${name} / frameWidth_${name}) BOR 0);
  calc(frames_${name}, xFrames_${name} * ((imageHeight_${name} / frameHeight_${name}) BOR 0));
  calc(hotspot[${name}].crop, '0|0|' + frameWidth_${name} + '|' + frameHeight_${name});
  
  clearinterval(crop_anim_${name});
  setinterval(crop_anim_${name}, calc(1.0 / frameRate_${name}),
    if(hotspot[${name}].loaded,
      inc(frame_${name});
      if(frame_${name} GE frames_${name}, set(frame_${name}, 0););
      mod(xPos_${name}, frame_${name}, xFrames_${name});
      div(yPos_${name}, frame_${name}, xFrames_${name});
      Math.floor(yPos_${name});
      mul(xPos_${name}, frameWidth_${name});
      mul(yPos_${name}, frameHeight_${name});
      calc(
        hotspot[${name}].crop,
        xPos_${name} + '|' + yPos_${name} + '|' + frameWidth_${name} + '|' + frameHeight_${name}
      );
    ,
      clearinterval(crop_anim_${name});
    );
  );
`;

export const executeImageStrip = (
  name: string,
  frameRate: number,
  frameWidth: number,
  frameHeight: number
) => `
  def(imageWidth_${name}, integer, get(hotspot[${name}].imagewidth));
  def(imageHeight_${name}, integer, get(hotspot[${name}].imageheight));
  def(frameWidth_${name}, integer, ${frameWidth});
  def(frameHeight_${name}, integer, ${frameHeight});
  def(frameRate_${name}, integer, ${frameRate});
  def(frame_${name}, integer, 0);

  calc(xFrames_${name}, (imageWidth_${name} / frameWidth_${name}) BOR 0);
  calc(frames_${name}, xFrames_${name} * ((imageHeight_${name} / frameHeight_${name}) BOR 0));
  calc(hotspot[${name}].crop, '0|0|' + frameWidth_${name} + '|' + frameHeight_${name});
  
  setinterval(crop_anim_${name}, calc(1.0 / frameRate_${name}),
    if(hotspot[${name}].loaded,
      inc(frame_${name});
      if(frame_${name} GE frames_${name}, set(frame_${name}, 0););
      mod(xPos_${name}, frame_${name}, xFrames_${name});
      div(yPos_${name}, frame_${name}, xFrames_${name});
      Math.floor(yPos_${name});
      mul(xPos_${name}, frameWidth_${name});
      mul(yPos_${name}, frameHeight_${name});
      calc(
        hotspot[${name}].crop,
        xPos_${name} + '|' + yPos_${name} + '|' + frameWidth_${name} + '|' + frameHeight_${name}
      );
    ,
      clearinterval(crop_anim_${name});
    );
  );
`;

const staticImagePoiIcon = (name: string) => `
  clearinterval(crop_anim_${name});
  set(hotspot[${name}].crop, '');
`;

export const reInitStaticImage = (krpano: KrpanoTypes, name: string) => {
  krpano.call(`set(hotspot[${name}].onloaded, '')`);
  krpano.call(staticImagePoiIcon(name));
};

export const refreshPoiIcon = (krpano: KrpanoTypes, name: string, prevConfig, config) => {
  const prevIcon = prevConfig.icon || {};
  const icon = config.icon || {};
  const hp = krpano.get(`hotspot[${name}]`);

  if (icon.imageStrip !== prevIcon.imageStrip) {
    // On change image type (Static Image/Image Strip Animation)
    if (icon.imageStrip) {
      initImageStripPoiIcon(krpano, name, icon, config.frameRate);
      hp.onloaded = imageStripAnimationPoiIcon(name);
    } else {
      reInitStaticImage(krpano, name);
    }
  } else if (config.frameRate !== prevConfig.frameRate) {
    // On change frameRate
    if (icon.imageStrip) {
      initImageStripPoiIcon(krpano, name, null, config.frameRate);
      krpano.call(imageStripAnimationPoiIcon(name));
    }
  } else if (icon.id !== prevIcon.id) {
    // On change Image resource
    if (icon.imageStrip) {
      initImageStripPoiIcon(krpano, name, icon);
    }
  }
};

export const getViewByDevice = (deviceViewer: string): { fovtype: string; fov: number } => {
  const data = { fovtype: 'MFOV', fov: 90 };
  if (DEVICE_VIEWER_ASPECT.desktop === deviceViewer) {
    data.fovtype = 'VFOV';
    data.fov = 65;
  }

  return data;
};

export const changeAspect = (tour: Tour, value: string) => {
  if (tour) {
    tour.deviceViewerAspectRatio = value;
    const viewData = getViewByDevice(value);
    const view = tour.krpano?.get('view');
    view.fovtype = viewData.fovtype;
    view.fov = viewData.fov;

    if (value === '0') {
      tour.krpano.call(`set(aspect, 0)`);
    } else {
      const vals = value.split(':');
      tour.krpano.call(`div(aspect, ${vals[0]}, ${vals[1]})`);
    }

    tour.krpano.call(`
      if(aspect === null, set(aspect,0));
      if(aspect LE 0,
      copy(destwidth, stagewidth);
      copy(destheight, stageheight);
       ,
      math.max(maxlength, stagewidth, stageheight);
      if(aspect GE 1.0,
      copy(destheight, maxlength);
      mul(destwidth, maxlength, aspect);
        ,
      div(destheight, maxlength, aspect);
      copy(destwidth, maxlength);
      );

      if(destwidth GT stagewidth, div(dwnscale,stagewidth,destwidth), set(dwnscale,1.0) );
      mul(destwidth,  destwidth, dwnscale);
      mul(destheight, destheight, dwnscale);
      if(destheight GT stageheight, div(dwnscale,stageheight,destheight), set(dwnscale,1.0) );
      mul(destwidth,  destwidth, dwnscale);
      mul(destheight, destheight, dwnscale);
      );
      sub(destx, stagewidth,  destwidth);
      sub(desty, stageheight, destheight);
      mul(destx, destx, 0.5);
      mul(desty, desty, 0.5);
      set(in_area_tween,true);
      tween(area.x,      get(destx),      0.5, easeInOutSine);
      tween(area.y,      get(desty),      0.5, easeInOutSine);
      tween(area.width,  get(destwidth),  0.5, easeInOutSine);
      tween(area.height, get(destheight), 0.5, easeInOutSine, WAIT);
      set(in_area_tween,false);
    `);
  }
};

const getPopupPositionOffset = (position: string, laserRange: number): string => {
  if (laserRange > 0) {
    return '0px';
  }

  let res: number;
  switch (position) {
    case POSITION_POI.TOP:
      res = (POPUP_ELEMENT_SIZE.POI / 2 + POPUP_ELEMENT_SIZE.DOT) / 2;
      break;
    case POSITION_POI.RIGHT:
      res = -(POPUP_ELEMENT_SIZE.POI / 2 + POPUP_ELEMENT_SIZE.DOT) / 2;
      break;
    case POSITION_POI.BOTTOM:
      res = -(POPUP_ELEMENT_SIZE.POI + POPUP_ELEMENT_SIZE.DOT) / 2;
      break;
    case POSITION_POI.LEFT:
      res = (POPUP_ELEMENT_SIZE.POI + POPUP_ELEMENT_SIZE.DOT) / 2;
      break;
    default:
      res = 0;
      break;
  }

  return `${res}px`;
};

export const getHtmlLaserTooltip = (poi: Poi, lang = 'en') => {
  let order = 0;
  let tX: string;
  let tY: string;
  let top: string;
  let left: string;
  let flexDirection = 'row';
  const { position, overviewPosition, laserRange } = poi;

  if (
    overviewPosition === OVERVIEW_POSITION_POI.BOTTOM_LEFT_CORNER &&
    position === POSITION_POI.NONE
  ) {
    return `
      [div class="vttc-v3-poi-laser-tooltip"]
        [div class="vttc-v3-poi-laser-tooltip__focusing-point"][/div]
      [/div]
    `;
  }

  if (position === POSITION_POI.TOP || position === POSITION_POI.BOTTOM) {
    flexDirection = 'column';
    left = '50%';
    tX = '-50%';
    tY = '0px';
    top = 'unset';
    order = 0;

    if (position === POSITION_POI.BOTTOM) {
      tY = `calc(100% + ${getPopupPositionOffset(position, laserRange)})`;
      order = 1;
    } else {
      tY = `${getPopupPositionOffset(position, laserRange)}`;
    }
  } else if (position === POSITION_POI.LEFT) {
    tX = `calc(-100% + ${getPopupPositionOffset(position, laserRange)})`;
    tY = 'calc(50% - 4px)'; // 4px is half width dot poi
  } else {
    order = 1;
    tX = `${getPopupPositionOffset(position, laserRange)}`;
    tY = 'calc(50% - 4px)'; // 4px is half width dot poi
  }

  // Other case: return laser tooltip html string
  return `
    [div class="vttc-v3-poi-laser-tooltip vttc-v3-poi-laser-tooltip--${
      getLaserSlashSize(poi) || '1'
    }"]
    [div class="vttc-v3-poi-laser-tooltip__point${laserRange === 0 ? ' invisible' : ''}"][/div]
      [div class="vttc-v3-poi-laser-tooltip__body" style="left: ${left}; top: ${top}; transform: translate(${tX}, ${tY})"]
        [div class="vttc-v3-poi-laser-tooltip-content" style="flex-direction: ${flexDirection}"]
          ${getHtmlLaserContent(poi, lang, order)}
          ${getHtmlLPinPopup(poi)}
        [/div]
      [/div]
    [/div]
  `;
};

export const buildHtmlHoverLaserTooltipPoi = (poi: Poi, content: string, handcursor = true) => {
  poi.krpano.call(`
    addhotspot(${poi.popupCode});
    set(hotspot[${poi.popupCode}].type, text);
    set(hotspot[${poi.popupCode}].renderer, css3d);
    set(hotspot[${poi.popupCode}].padding, 0);
    set(hotspot[${poi.popupCode}].bgalpha, 0);
    set(hotspot[${poi.popupCode}].autowidth, true);
    set(hotspot[${poi.popupCode}].autoheight, true);
    set(hotspot[${poi.popupCode}].rx,0);
    set(hotspot[${poi.popupCode}].ry,0);
    set(hotspot[${poi.popupCode}].rz,0);
    set(hotspot[${poi.popupCode}].rotate,0);
    set(hotspot[${poi.popupCode}].capture, false);
    set(hotspot[${poi.popupCode}].capturetouch, false);
    set(hotspot[${poi.popupCode}].distorted, false);
    set(hotspot[${poi.popupCode}].handcursor, ${handcursor});
    set(hotspot[${poi.popupCode}].css, font-family:Barlow;font-size:14px;color:#ffffff;);
    set(hotspot[${poi.popupCode}].atv, '${poi.atv}');
    set(hotspot[${poi.popupCode}].ath, '${poi.ath}');
    set(hotspot[${poi.popupCode}].zorder, '${POI_Z_ORDER.popup}');
    set(hotspot[${poi.popupCode}].visible, ${poi.previewed});
  `);
  poi.krpano.set(`hotspot[${poi.popupCode}].html`, content);
};

const getLaserSlashSize = (poi: Poi) => {
  const { width, height, laserRange = DEFAULT_LASER_RANGE, scale, distorted } = poi;
  const maxSlashSize = LASER_TOOLTIP_SLASH_MAX_SIZE;

  let radius = ((width + height) * scale ** 2) / 2 / 2;
  if (distorted && !isMobileDevice(poi.krpano)) {
    radius *= Math.sqrt(2);
  }

  const radiusRounded = Math.round(radius * laserRange);

  return radiusRounded < maxSlashSize ? radiusRounded : maxSlashSize;
};

const getHtmlLaserContent = (
  poi: any, // TODO: Define type
  lang = 'en',
  order: number
) => {
  const { type: poiType } = poi;

  let title = '';
  let subTitle = '';
  let mediaUrl = '';
  let imageOriginHeight = '';
  let isVideo = false;

  if (poiType === POI_TYPE.ITEM_INFO) {
    const hoverType = getItemInfoHoverType(poi);
    const { itemInfo } = poi;
    const { icon, name, distance, distanceUnit, price, currencyUnit, unit } = itemInfo;
    mediaUrl = icon?.url;
    title = name;
    switch (hoverType) {
      case ITEM_INFO_TEMPLATE.LOCATION:
        subTitle = `${distance} ${distanceUnit}`;
        break;

      case ITEM_INFO_TEMPLATE.BRAND:
        imageOriginHeight = 'unset';

        break;

      case ITEM_INFO_TEMPLATE.PRODUCT:
        if (price) {
          subTitle = formatPriceProduct(convertCurrencyVnd(price), currencyUnit, unit);
        }

        break;

      case ITEM_INFO_TEMPLATE.FACILITY:
        subTitle = '';
        break;

      default:
        console.warn(`Type[${hoverType}] is not handled hover`);
        return ''; // Default return empty string
    }
  } else if (poiType === POI_TYPE.PROMOTION) {
    const { promotion } = poi;
    const { name, endDate, image } = promotion;
    const dateFormat = moment(endDate).format('DD/MM/YYYY');
    title = name;
    subTitle = `${lang === 'vi' ? 'đến' : 'to'} ${dateFormat} `;
    mediaUrl = image?.url;
  } else if (poiType === POI_TYPE.GALLERY) {
    const { gallery } = poi;
    if (gallery[0]?.type === GALLERY_TYPE.VIDEO) {
      isVideo = true;
    }
    mediaUrl = gallery[0]?.file?.url;
  }

  const media = isVideo
    ? ` 
        [div class="popup__video" ]
          [video class="video__content" ]
            [source src=${getVideoCDNUrl(mediaUrl)} type="video/mp4" /]
          [/video]
          [span class="fa fa-play video__icon"] [/span]
        [/div]
        `
    : mediaUrl &&
      `
    [img
      class="popup__image"
      style="height: ${imageOriginHeight}"
      src = "${getImageCDNUrl(mediaUrl)}"
    /]
  `;

  return `
    [div class="vttc-v3-poi-laser-tooltip-content__popup vttc-v3-poi-laser-tooltip-content__popup--${poi?.popupSize?.toLowerCase()}" style="order: ${order}"]
        ${media} 
      [div class="popup__info"]
        [div class="info__title"]${title} [/div]
        [div class= "info__subtitle"]${subTitle} [/div]
      [/div]
    [/div]
  `;
};

export const getHtmlLPinPopup = (poi: Poi) => {
  const { laserRange, position } = poi;

  let width = '1px';
  let height = '1px';
  let flexDirection = 'row';
  const size: number = LASER_RANGE_VALUE[laserRange];
  let orderPinContent = 1;
  let orderPinIcon = 2;

  if (position === POSITION_POI.TOP || position === POSITION_POI.BOTTOM) {
    flexDirection = 'column';
    height = `${size}px`;
  } else {
    width = `${size}px`;
  }

  if (position === POSITION_POI.LEFT || position === POSITION_POI.TOP) {
    orderPinIcon = 1;
    orderPinContent = 2;
  }

  // Other case: return laser tooltip html string
  return `
    [div class="vttc-v3-poi-laser-tooltip-content__pin" style="flex-direction: ${flexDirection};"]
      [div class= "pin__content pin__content--${laserRange}" style="width: ${width}; height: ${height}; order: ${orderPinContent} "][/div]
      [div class= "pin__icon" style="order: ${orderPinIcon}"]
        ${getHtmlIconImage(poi)}
      [/div]
    [/div]
    `;
};

const getHtmlIconImage = (poi: Poi) => {
  const sizeIcon = 32;
  const icon = poi?.getIcon();
  const url = poi?.getIconUrl();

  if (icon && icon.imageStrip) {
    const { file, frameWidth, frameHeight } = icon;
    const width = frameWidth ? file.width * (sizeIcon / frameWidth) : sizeIcon;
    const height = frameHeight ? file.height * (sizeIcon / frameHeight) : sizeIcon;

    return `[div class="icon__image-strip" style=" --height-image-trip: ${height}px; --width-image-trip: ${width}px" ]
              [img class="image-strip__content" style="background-image: url(${url})" /]
            [/div]`;
  }

  const image = url
    ? `
    [img
      class="icon__image"
      src="${url}"
    /]`
    : '';

  return image;
};

export const formatPriceProduct = (price, currencyUnitKey, unitKey) => {
  const currencyUnitValue = currencyUnitKey ? ` ${currencyUnitKey}` : '';
  const unitValue = unitKey ? `/${UNIT[unitKey]}` : '';

  return `${price}${currencyUnitValue}${unitValue}`;
};

const getItemInfoHoverType = (poi: any) => {
  // TODO: Define type
  const { itemInfo, hoverType } = poi;

  if (!itemInfo) {
    return false;
  }

  if (hoverType) {
    return hoverType;
  }

  let res: string = null;
  const { type } = itemInfo;
  switch (type) {
    case ITEM_INFO_TYPES.LOCATION:
      res = ITEM_INFO_TEMPLATE.LOCATION;
      break;

    case ITEM_INFO_TYPES.BRAND:
      res = ITEM_INFO_TEMPLATE.BRAND;
      break;

    case ITEM_INFO_TYPES.PRODUCT:
    case ITEM_INFO_TYPES.ECOMMERCE_PRODUCT:
      res = ITEM_INFO_TEMPLATE.PRODUCT;
      break;

    default:
      res = ITEM_INFO_TEMPLATE.NONE;
      break;
  }

  return res;
};

export const sphereToSpace = (
  krpano: KrpanoTypes,
  h: number,
  v: number,
  depth = 1000
): { x?: number; y?: number; z?: number } => {
  const id = Date.now();
  krpano.set(`sphere_h_${id}`, h);
  krpano.set(`sphere_v_${id}`, v);
  krpano.set(`sphere_d_${id}`, depth);
  krpano.call(
    `spheretospace('sphere_h_${id}', 'sphere_v_${id}', 'sphere_d_${id}', 'space_x_${id}', 'space_y_${id}', 'space_z_${id}')`
  );
  const x = krpano.get(`space_x_${id}`);
  const y = krpano.get(`space_y_${id}`);
  const z = krpano.get(`space_z_${id}`);
  return {
    x,
    y,
    z,
  };
};

export const getAutoScale = (krpano: KrpanoTypes, code: string): number => {
  const maxScale = 5;
  const minScale = 0.5;
  // get hotspot detail
  const hp = krpano.get(`hotspot[${code}]`);

  // get furthest
  const { x: x1, z: z1 } = sphereToSpace(krpano, 0, 0);
  const distanceFurthest = Math.sqrt(x1 * x1 + z1 * z1);
  const { x: x2, z: z2 } = sphereToSpace(krpano, 0, 90);
  // get Nearest
  const distanceNearest = Math.sqrt(x2 * x2 + z2 * z2);
  const delta = (minScale - maxScale) / (distanceFurthest - distanceNearest);
  const { x, z } = sphereToSpace(krpano, hp.ath, hp.atv);
  const distanceHotspot = Math.sqrt(x * x + z * z);

  return Number((delta * distanceHotspot + maxScale).toFixed(2));
};
